import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    inject,
    Input,
    OnDestroy,
    Output,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { AuthorityUtil, TagModel, TagUtil, UserContextService, WhoAmI } from '@libs/vc-core-lib';
import { translate, TranslocoPipe } from '@jsverse/transloco';
import { map, Subject, take, takeUntil } from 'rxjs';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { filter, flow, isEqual, last, partialRight, property, some } from 'lodash-es';
import { ReferenceTagService } from '@libs/vc-reference-data-lib';
import { BaseFieldComponent } from 'vcp-common-ui-lib';
import { MultiSelectWithGroupsComponent } from '../../multi-select-with-groups/multi-select-with-groups.component';
import { KeyValue } from '@angular/common';
import { TagIconPipe } from '../pipe/tag-icon/tag-icon.pipe';
import { ChipsAutocompleteWithGroupsComponent } from '../../chips-groups-autocomplete/chips-autocomplete-with-groups.component';

@Component({
    selector: 'vc-tags-selector',
    templateUrl: './tags-selector.component.html',
    styleUrls: ['./tags-selector.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TranslocoPipe, MultiSelectWithGroupsComponent, ChipsAutocompleteWithGroupsComponent],
    providers: [TagIconPipe],
})
export class TagsSelectorComponent
    extends BaseFieldComponent<KeyValue<string, TagModel[]>>
    implements AfterViewInit, OnDestroy
{
    private readonly _referenceTagService: ReferenceTagService = inject(ReferenceTagService);
    private readonly _userContextService: UserContextService = inject(UserContextService);
    private readonly _changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);
    private readonly _tagIconPipe: TagIconPipe = inject(TagIconPipe);

    private readonly _destroy$: Subject<void> = new Subject();
    private _user!: WhoAmI;
    private _hasInternalReadTagsPerm: boolean = false;
    private _hasInternalAssignTagsPerm: boolean = false;
    private _hasExternalReadTagsPerm: boolean = false;
    private _hasExternalCreateTagsPerm: boolean = false;
    private _hasExternalAssignTagsPerm: boolean = false;
    private _hasPrivateReadTagsPerm: boolean = false;
    private _hasPrivateAssignTagsPerm: boolean = false;

    readonly addNewTagText = translate('CORE.ADD_NEW_TAG');
    readonly descriptionText = translate('CORE.ADD_TAGS_DESC');

    separatorKeysCodes: number[] = [ENTER, COMMA];
    tags: KeyValue<string, TagModel[]>[] = [];
    options: TagModel[] = [];
    filterValue: string = '';

    @Input()
    set values(val: TagModel[]) {
        if (!isEqual(val, this._values)) {
            this._values = val;
            this._keyValues = this._flattenTags(val);
            this._valuesAsStrings = TagUtil.getTagsAsStrings(val);
            this.valuesChange.emit(val);
            this.valuesAsStringsChange.emit(this._valuesAsStrings);
            this._changeDetectorRef.markForCheck();
        }
    }
    get values(): TagModel[] {
        return this._values;
    }
    private _values!: TagModel[];

    @Input()
    set valuesAsStrings(tags: string[]) {
        if (!isEqual(tags, this._valuesAsStrings)) {
            this._valuesAsStrings = tags;
            this._values = TagUtil.convertArrayStringToTags(tags);
            this._keyValues = this._flattenTags(this._values);
            this.valuesChange.emit(this._values);
            this.valuesAsStringsChange.emit(this._valuesAsStrings);
            this._changeDetectorRef.markForCheck();
        }
    }
    private _valuesAsStrings!: string[];

    get keyValues(): KeyValue<string, TagModel>[] {
        return this._keyValues;
    }
    set keyValues(value: KeyValue<string, TagModel>[]) {
        this._keyValues = value;
        this._values = value?.map((kv) => kv.value) ?? [];
        this._valuesAsStrings = TagUtil.getTagsAsStrings(this._values);
        this.valuesChange.emit(this._values);
        this.valuesAsStringsChange.emit(this._valuesAsStrings);
        this._changeDetectorRef.markForCheck();
    }
    private _keyValues!: KeyValue<string, TagModel>[];

    @Input()
    showInternalTags = true;

    @Input()
    label: string = translate('COMMON_UI.TAGS');

    @Input()
    placeholder!: string;

    @Input()
    ariaDescribedBy!: string;

    @Input()
    allowSelection: boolean = false;

    @Input()
    allowAddNew: boolean = true;

    @Input()
    addNewLabel: string = this.addNewTagText;

    @Input()
    showAddDescription: boolean = true;

    @Input()
    showClear: boolean = false;

    @Input()
    suffixIcon!: string;

    @Output()
    valuesChange = new EventEmitter<TagModel[]>();

    @Output()
    valuesAsStringsChange = new EventEmitter<string[]>();

    @ViewChild('chipsAutocompleteWithGroups')
    chipsAutocompleteWithGroupsComponent!: ChipsAutocompleteWithGroupsComponent<TagModel>;

    // verifies the user can select External Tags, by either using allowSelection mode or having Assign Perm as well as Read Permission
    get checkExternalSelect(): boolean {
        return this._hasExternalReadTagsPerm && (this._hasExternalAssignTagsPerm || this.allowSelection);
    }

    // verifies user can select Internal Tags by either using allowSelection mode or having Assign Perm as well as Read Permission
    get checkInternalSelect(): boolean {
        return (
            this.showInternalTags &&
            this._hasInternalReadTagsPerm &&
            (this._hasInternalAssignTagsPerm || this.allowSelection)
        );
    }

    // verifies user can select Private Tags by either using allowSelection mode or having Assign Perm as well as Read Permission
    get checkPrivateSelect(): boolean {
        return this._hasPrivateReadTagsPerm && (this._hasPrivateAssignTagsPerm || this.allowSelection);
    }

    constructor() {
        super();
        this._userContextService
            .getContext()
            .pipe(takeUntil(this._destroy$))
            .subscribe((user: WhoAmI | null) => {
                if (user) {
                    this._hasInternalReadTagsPerm = user.hasAuthority(AuthorityUtil.PERMISSION_TAG_INTERNAL_READ);
                    this._hasInternalAssignTagsPerm = user.hasAuthority(AuthorityUtil.PERMISSION_TAG_INTERNAL_ASSIGN);
                    this._hasExternalReadTagsPerm = user.hasAuthority(AuthorityUtil.PERMISSION_TAG_EXTERNAL_READ);
                    this._hasExternalAssignTagsPerm = user.hasAuthority(AuthorityUtil.PERMISSION_TAG_EXTERNAL_ASSIGN);
                    this._hasExternalCreateTagsPerm = user.hasAuthority(AuthorityUtil.PERMISSION_TAG_EXTERNAL_CREATE);
                    this._hasPrivateReadTagsPerm = user.hasAuthority(AuthorityUtil.PERMISSION_TAG_PRIVATE_READ);
                    this._hasPrivateAssignTagsPerm = user.hasAuthority(AuthorityUtil.PERMISSION_TAG_PRIVATE_ASSIGN);
                    this._user = user;
                }
            });
    }

    ngAfterViewInit() {
        if (this._user) {
            this._getTags();
        }
    }

    refreshTags() {
        this._getTags();
    }

    addNewTag(tag: string): void {
        const value = (tag || '').trim();
        if (value) {
            const nameAndValue: string[] = value.split(TagUtil.TAG_VALUE_SEP);
            const tag = {} as TagModel;
            tag.name = nameAndValue[0];
            tag.isNew = true;
            tag.tagValue = TagUtil.EXT_TAG + value;
            tag.prefix = TagUtil.EXT_TAG;
            tag.displayPrefix = TagUtil.EXT_TAG;
            tag.displayValue = value;
            if (nameAndValue && nameAndValue.length > 1) {
                tag.value = last(nameAndValue) ?? '';
            }

            const existsInTags = some(
                this.tags,
                flow(property('tags'), partialRight(some, { name: tag.name, value: tag.value }))
            );
            const existsInSelected = this.values?.some(
                (value: TagModel) => value.name === tag.name && value.value === tag.value
            );
            if (!existsInSelected && !existsInTags) {
                this.values = [...(this.values ?? []), tag];
            }
        }

        this.chipsAutocompleteWithGroupsComponent.filterValue = '';
        this.chipsAutocompleteWithGroupsComponent.autocompleteFilterInput.nativeElement.value = '';
    }

    removeTag(tag: TagModel): void {
        this.values = filter(this.values, (value: TagModel) => !isEqual(tag, value));
        this.chipsAutocompleteWithGroupsComponent.filterValue = '';
        this.chipsAutocompleteWithGroupsComponent.autocomplete.closePanel();
        this._changeDetectorRef.markForCheck();
    }

    private _checkEditPermissions(tag: TagModel): boolean {
        if (this.readonly) {
            return false;
        }
        if (tag.isNew || this.allowSelection) {
            return true;
        }
        return (
            (this.checkInternalSelect && (tag.prefix === '/vc' || tag.prefix === '/vc/')) ||
            (this.checkExternalSelect && (tag.prefix === '/ext' || tag.prefix === '/ext/')) ||
            (this.checkPrivateSelect && (tag.prefix === '/pvt' || tag.prefix === '/pvt/'))
        );
    }

    private _getTags() {
        this._referenceTagService
            .getTags(this._user.getOrgId(), this.checkExternalSelect, this.checkInternalSelect, this.checkPrivateSelect)
            .pipe(
                take(1),
                map((tags: TagModel[]) => TagUtil.convertObjectArrayToTags(TagUtil.format(tags)))
            )
            .subscribe((values: TagModel[]) => {
                this.options = values;
                this.tags = this._getGroupedTags(values);
                this._changeDetectorRef.markForCheck();
            });
    }

    private _flattenTags(values: TagModel[]): KeyValue<string, TagModel>[] {
        if (!values) return [];
        return values.map((item: TagModel) => ({
            key: item.displayPrefix,
            value: item,
        }));
    }

    private _getGroupedTags(values: TagModel[]): KeyValue<string, TagModel[]>[] {
        const map = new Map<string, TagModel[]>();
        const tags: KeyValue<string, TagModel[]>[] = [];
        values.forEach((item: TagModel) => {
            const key = item.displayPrefix;
            const collection = map.get(key);
            if (!collection) {
                map.set(key, [item]);
            } else {
                collection.push(item);
            }
        });
        map.forEach((value: TagModel[], key: string) => {
            tags.push({
                key: key,
                value: value,
            });
        });
        return tags;
    }

    tooltipOptionRenderer = (value: TagModel) => `${value.displayValue} (${value.displayPrefix})`;
    tagRenderer = (value: TagModel): string => `${value.name}`;
    chipIconRenderer = (value: TagModel): string => this._tagIconPipe.transform(value.prefix ?? '');
    removeHandler: (tag: TagModel) => boolean = (tag: TagModel) => this._checkEditPermissions(tag);

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }
}
